import { Injectable } from '@angular/core';
import { APIHelper, DataGetOptions } from '@controladad/ng-base';
import {
  UserAdminCreate,
  UserAdminUpdate,
  UserChangeStatus,
  UserCustomerCreate,
  UserCustomerUpdate,
  UserDelete,
  UserEntity,
  UserVendorCreate,
  UserVendorDetail,
  UserVendorUpdate,
  VendorInfo,
  VendorItem,
  VendorProductsEntity,
} from '@models';
import { GetApiAdapter } from '../adapters';
import { Roles } from '../data';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  // ADAPTERS
  getAdminAdapter(options?: DataGetOptions<UserEntity>) {
    return GetApiAdapter<UserEntity>(`/AdminPanel/GetAdminList`, options);
  }

  getCustomerAdapter(options?: DataGetOptions<UserEntity>) {
    return GetApiAdapter<UserEntity>(`/AdminPanel/GetCustomerList`, options);
  }

  getVendorAdapter(options?: DataGetOptions<VendorItem>) {
    return GetApiAdapter<VendorItem>(`/AdminPanel/GetVendorList`, options);
  }

  // ADMIN
  createAdmin(model: UserAdminCreate) {
    return http$.post(`/AdminPanel/AddAdmin`, { fields: model });
  }

  getAdminDetails(id: number) {
    return http$.get<{ adminDetailsDTO: UserEntity }>(`/AdminPanel/GetAdminDetails?AdminId=${id}`);
  }

  updateAdmin(model: UserAdminUpdate) {
    const nestedKeys = APIHelper.nestValues(model);
    const data = { id: model.id, editFields: nestedKeys };

    return http$.put(`/AdminPanel/EditAdmin`, data);
  }

  // VENDOR
  createVendor(model: UserVendorCreate) {
    return http$.post(`/AdminPanel/AddVendor`, { fields: model });
  }

  createVendorFromUser(userId: number, vendorInfo: VendorInfo) {
    return http$.post('/AdminPanel/CreateVendorFromUser', { fields: { userId, ...vendorInfo } });
  }

  getVendorDetails(id: number) {
    return http$.get<{ vendorDetailsDTO: UserVendorDetail }>(`/AdminPanel/GetVendorDetails?VendorId=${id}`);
  }

  getVendorProducts(vendorId?: number, options?: DataGetOptions<VendorProductsEntity>) {
    return GetApiAdapter<VendorProductsEntity>(`/AdminPanel/GetVendorProducts?VendorId=${vendorId}`, options);
  }

  updateVendor(model: Partial<UserVendorUpdate>) {
    const editFields = APIHelper.nestValues(model);
    return http$.put(`/AdminPanel/EditVendor`, { id: model.id, editFields });
  }

  deleteVendor(ids: number | number[]) {
    const arrayIds = typeof ids === 'number' ? [ids] : ids;
    return http$.delete('/AdminPanel/DeleteVendor', { body: { vendorIds: arrayIds } });
  }

  changeVendorStatus(model: UserChangeStatus) {
    return http$.patch(`/AdminPanel/ChangeVendorStatus`, model);
  }

  // CUSTOMER
  getCustomerDetails(id: number) {
    return http$.get<{ customerDetailsDTO: UserEntity }>(`/AdminPanel/GetCustomerDetails?CustomerId=${id}`);
  }

  createCustomer(model: UserCustomerCreate) {
    return http$.post(`/AdminPanel/AddCustomer`, { fields: model });
  }

  updateCustomer(model: UserCustomerUpdate) {
    const nestedKeys = APIHelper.nestValues(model);
    const data = { id: model.id, editFields: nestedKeys };

    return http$.put(`/AdminPanel/EditCustomer`, data);
  }

  // Common
  delete(model: UserDelete) {
    if (typeof model.userIds === 'number') {
      model.userIds = [model.userIds];
    }
    return http$.delete(`/AdminPanel/DeleteUsers`, {
      body: model,
    });
  }

  changeStatus(model: UserChangeStatus) {
    return http$.patch(`/AdminPanel/ChangeCustomerStatus`, model);
  }

  forceLogout(id: number, role: Roles) {
    const deadline = new Date();
    return http$.post(`/AdminPanel/AddTokenDeadline`, {
      userId: id,
      deadline: deadline,
      role: role,
    });
  }
}
